import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  FormLabel,
  FormControl,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import { isMobile } from "react-device-detect";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { getIn } from "formik";
import { decryptText } from "../../utilis/encryption";

export default function GoogleMapsAutocomplete({
  name,
  formik,
  onSelect,
  label = "",
  required = false,
  disabled = false,
  placeholder = "",
  variant = "outlined",
  TextFieldProps = {},
}) {
  const currentValue = getIn(formik.values, name) || "";
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(currentValue);
  const [open, setOpen] = useState(false);

  const debounceTimer = useRef(null);
  const cancelSource = useRef(null);
  const fetchSuggestions = useCallback(async (val) => {
    if (!val?.trim()) {
      setOptions([]);
      setOpen(false);
      return;
    }

    cancelSource.current?.cancel?.();
    cancelSource.current = axios.CancelToken.source();

    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/google/autocomplete`,
        {
          params: { input: val },
          cancelToken: cancelSource.current.token,
        }
      );
      let decrypted = await decryptText(data?.response?.data);

      let suggestions =
      typeof decrypted === "string" ? JSON.parse(decrypted) : decrypted;
      // Guarantee array
      if (!Array.isArray(suggestions)) {
        suggestions = [];
      }
      console.log("suggestions", suggestions);
      setOptions(suggestions);
      setOpen(suggestions.length > 0);
    } catch {
      setOptions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (event, val, reason) => {
      if (reason === "input") {
        setInputValue(val);

        clearTimeout(debounceTimer.current);
        if (!val) {
          setOptions([]);
          setOpen(false);
          formik.setFieldValue(name, "");
          return;
        }

        debounceTimer.current = setTimeout(() => fetchSuggestions(val), 500);
      } else if (reason === "clear") {
        setInputValue("");
        setOptions([]);
        setOpen(false);
        formik.setFieldValue(name, "");
      }
    },
    [fetchSuggestions, formik, name]
  );

  const handleSelect = useCallback(
    async (event, newValue) => {
      if (!newValue?.placeId) return;

      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/google/address-details/${newValue.placeId}`
        );
        let decrypted = await decryptText(data?.response?.data);

        // safe parse
        let details =
          typeof decrypted === "string" ? JSON.parse(decrypted) : decrypted;

        if (!details || typeof details !== "object") {
          details = {};
        }

        const areaName = details?.areaName || newValue?.description;

        setInputValue(areaName);
        formik.setFieldValue(name, areaName);
        onSelect?.(details);
        setOpen(false);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [formik, name, onSelect]
  );

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimer.current);
      cancelSource.current?.cancel?.();
    };
  }, []);

  useEffect(() => {
    if (currentValue && currentValue !== inputValue) {
      setInputValue(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  return (
    <FormControl fullWidth>
      {label && (
        <FormLabel
          htmlFor={name}
          sx={{
            color: "#1C252E",
            fontSize: { xs: "13px", md: "14px" },
            fontWeight: 500,
            lineHeight: "22px",
            mb: 0.75,
          }}
        >
          {label}
          {required && <span className="asteriskColor"> *</span>}
        </FormLabel>
      )}
      <Autocomplete
        fullWidth
        open={open}
        onOpen={() => setOpen(options?.length > 0)}
        onClose={() => setOpen(false)}
        options={options}
        loading={loading}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        getOptionLabel={(option) => option?.description || ""}
        filterOptions={(x) => x}
        noOptionsText=""
        popupIcon={null}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            id={name}
            name={name}
            placeholder={placeholder}
            variant={variant}
            disabled={disabled}
            error={
              getIn(formik.touched, name) && Boolean(getIn(formik.errors, name))
            }
            helperText={
              getIn(formik.touched, name) && getIn(formik.errors, name)
            }
            {...TextFieldProps}
            InputProps={{
              ...params.InputProps,
              ...TextFieldProps.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 44,
                borderRadius: "8px",
                padding: "10px 14px 10px 0px",
                boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                fontSize: "14px",
                fontWeight: 400,
                backgroundColor: disabled ? "#f9fafb " : "inherit",
              },
              "& .MuiInputBase-input": {
                height: "100%",
                fontSize: isMobile ? "16px" : "14px",
              },
              "& .MuiInputBase-input::placeholder": {
                textTransform: "initial",
                fontSize: "14px",
                color: "#4F5E71",
              },
              "& .MuiInputBase-input.Mui-disabled": {
                cursor: "not-allowed",
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option?.placeId}
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #eee",
              py: 1,
              px: 1.5,
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <LocationOnIcon sx={{ mr: 1, color: "gray" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option?.description || ""}
            </Typography>
          </Box>
        )}
      />
    </FormControl>
  );
}